import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Gift,
  Check,
  Clock,
  X,
  User,
  Mail,
  DollarSign,
  Calendar
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Button, Badge, LoadingSpinner } from './components';

function GiftCardsPage() {
  // State
  const [giftCards, setGiftCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('unredeemed');
  const [staff, setStaff] = useState([]);

  // Modal state
  const [selectedCard, setSelectedCard] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [redeemNotes, setRedeemNotes] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');

  // Fetch gift cards from API
  useEffect(() => {
    loadGiftCards();
    loadStaff();
  }, [statusFilter, searchTerm]);

  const loadGiftCards = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter === 'redeemed') params.is_redeemed = 'true';
      if (statusFilter === 'unredeemed') params.is_redeemed = 'false';

      const data = await apiService.getGiftCards(params);

      setGiftCards(data.results || []);
      setStats(data.stats || null);
    } catch (err) {
      setError(err.message || 'Failed to load gift cards');
      console.error('Error loading gift cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const data = await apiService.getStaff({ is_technician: true });
      setStaff(data.results || data || []);
    } catch (err) {
      console.error('Error loading staff:', err);
    }
  };

  const openRedeemModal = (card) => {
    setSelectedCard(card);
    setShowRedeemModal(true);
    setSelectedStaffId('');
    setRedeemNotes('');
    setRedeemError('');
  };

  const closeRedeemModal = () => {
    setShowRedeemModal(false);
    setSelectedCard(null);
    setSelectedStaffId('');
    setRedeemNotes('');
    setRedeemError('');
  };

  const handleRedeem = async () => {
    if (!selectedStaffId) {
      setRedeemError('Please select a staff member');
      return;
    }

    setRedeeming(true);
    setRedeemError('');

    try {
      await apiService.redeemGiftCard(selectedCard.id, {
        staff_id: parseInt(selectedStaffId),
        notes: redeemNotes
      });

      closeRedeemModal();
      loadGiftCards();
    } catch (err) {
      setRedeemError(err.message || 'Failed to redeem gift card');
    } finally {
      setRedeeming(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  // Filter status options
  const filterOptions = [
    { value: 'unredeemed', label: 'Available' },
    { value: 'redeemed', label: 'Redeemed' },
    { value: 'all', label: 'All' }
  ];

  if (loading && giftCards.length === 0) {
    return (
      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Gift className="w-6 h-6 text-red-800" />
            Gift Cards
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage and redeem gift card purchases</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Panel>
            <div className="p-4">
              <div className="text-sm text-gray-500">Total Gift Cards</div>
              <div className="text-2xl font-bold text-gray-800">{stats.total || 0}</div>
              <div className="text-sm text-gray-500">{formatCurrency(stats.total_value)}</div>
            </div>
          </Panel>
          <Panel>
            <div className="p-4">
              <div className="text-sm text-gray-500">Available to Redeem</div>
              <div className="text-2xl font-bold text-green-600">{stats.unredeemed || 0}</div>
              <div className="text-sm text-gray-500">{formatCurrency(stats.unredeemed_value)}</div>
            </div>
          </Panel>
          <Panel>
            <div className="p-4">
              <div className="text-sm text-gray-500">Redeemed</div>
              <div className="text-2xl font-bold text-gray-400">{stats.redeemed || 0}</div>
              <div className="text-sm text-gray-500">{formatCurrency(stats.redeemed_value)}</div>
            </div>
          </Panel>
        </div>
      )}

      {/* Search and Filters */}
      <Panel className="mb-6">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Panel>

      {/* Gift Cards Table */}
      <Panel>
        {error ? (
          <div className="p-8 text-center text-red-500">{error}</div>
        ) : giftCards.length === 0 ? (
          <div className="p-8 text-center">
            <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No gift cards found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gift Card
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sender
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {giftCards.map((card) => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-red-800" />
                        <span className="font-medium text-gray-900">{card.gift_card_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{card.recipient_name}</div>
                      <div className="text-sm text-gray-500">{card.recipient_email}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{card.sender_name}</div>
                      <div className="text-sm text-gray-500">{card.sender_email}</div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-lg font-semibold text-gray-900">${card.amount}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {card.is_redeemed ? (
                        <Badge variant="gray">
                          <Check className="w-3 h-3 mr-1" />
                          Redeemed
                        </Badge>
                      ) : (
                        <Badge variant="green">
                          <Clock className="w-3 h-3 mr-1" />
                          Available
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(card.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {!card.is_redeemed && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openRedeemModal(card)}
                        >
                          Redeem
                        </Button>
                      )}
                      {card.is_redeemed && card.redeemed_by_name && (
                        <span className="text-xs text-gray-400">
                          by {card.redeemed_by_name}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Redeem Modal */}
      {showRedeemModal && selectedCard && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={closeRedeemModal}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Redeem Gift Card</h2>
                <button
                  onClick={closeRedeemModal}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Gift Card Info */}
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="w-8 h-8 text-red-800" />
                    <div>
                      <div className="font-semibold text-gray-900">{selectedCard.gift_card_name}</div>
                      <div className="text-2xl font-bold text-red-800">${selectedCard.amount}</div>
                    </div>
                  </div>
                </div>

                {/* Recipient Info */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Recipient:</span>
                    <span className="font-medium">{selectedCard.recipient_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">{selectedCard.recipient_email}</span>
                  </div>
                </div>

                {/* Staff Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member *
                  </label>
                  <select
                    value={selectedStaffId}
                    onChange={(e) => setSelectedStaffId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                  >
                    <option value="">Select staff member...</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.person?.first_name} {s.person?.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={redeemNotes}
                    onChange={(e) => setRedeemNotes(e.target.value)}
                    placeholder="Any notes about this redemption..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                    rows={2}
                  />
                </div>

                {/* Error */}
                {redeemError && (
                  <div className="text-red-500 text-sm bg-red-50 p-2 rounded">
                    {redeemError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                <Button variant="secondary" onClick={closeRedeemModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleRedeem}
                  disabled={redeeming}
                >
                  {redeeming ? 'Processing...' : 'Confirm Redemption'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default GiftCardsPage;
